<script setup>
import SegmentView from "@/components/SegmentView.vue";
import {computed, ref, watch} from "vue";
import {FIXED_GRID_SIZE, getCSSVariable} from "@/composables/challenges";

const props = defineProps(['isValid', 'segments', 'errorMessage', 'gameBoardClass', 'boardKey']);

const originKey = ref(0);
const boardBg = ref('transparent');
const boardRoot = ref(null);
let bgTimer = null;

watch(() => [props.segments, props.boardKey], () => {
  originKey.value++;
  boardBg.value = 'transparent';
  clearTimeout(bgTimer);
  bgTimer = setTimeout(() => { boardBg.value = '#1e1e1e'; }, 1600);
}, { immediate: true });

const gridDims = computed(() => {
  void originKey.value;
  // CSS custom properties inherit, so reading from the board's own root
  // element (rather than document.documentElement) picks up any local
  // override set by an ancestor (e.g. TutorialBoard's --colAmount/--rowAmount).
  const styleSource = boardRoot.value ?? document.documentElement;
  const cols = Number.parseInt(getCSSVariable('--colAmount', styleSource)) || FIXED_GRID_SIZE;
  const rows = Number.parseInt(getCSSVariable('--rowAmount', styleSource)) || FIXED_GRID_SIZE;
  return { cols, rows };
});

const delayMap = computed(() => {
  const { cols, rows } = gridDims.value;

  // Pick a random origin: 4 corners + center
  const origins = [
    [0, 0], [0, cols - 1], [rows - 1, 0], [rows - 1, cols - 1],
    [(rows - 1) / 2, (cols - 1) / 2],
  ];
  const [or, oc] = origins[Math.floor(Math.random() * origins.length)];

  const map = [];
  const maxDist = Math.sqrt(Math.pow(rows - 1, 2) + Math.pow(cols - 1, 2));
  for (let i = 0; i < (props.segments?.length ?? 0); i++) {
    const r = Math.floor(i / cols);
    const c = i % cols;
    const dist = Math.sqrt(Math.pow(r - or, 2) + Math.pow(c - oc, 2));
    map.push(`${Math.round((dist / maxDist) * 900)}ms`);
  }
  return map;
});

function cellClass(index) {
  const { cols, rows } = gridDims.value;
  const r = Math.floor(index / cols);
  const c = index % cols;
  return {
    'last-col': c === cols - 1,
    'last-row': r === rows - 1,
  };
}
</script>

<template>
  <div>
    <div v-show="isValid" ref="boardRoot" v-bind:class="gameBoardClass"
         :style="{ backgroundColor: boardBg, transition: 'background-color 1.2s ease' }">
      <!-- https://stackoverflow.com/questions/53698837/make-delay-between-item-render-transition-in-array-in-vue-js -->
      <TransitionGroup name="list">
        <SegmentView v-for="(seg, index) in segments"
                     :key="`${originKey}-${index}`"
                     :index=index
                     :segment=seg
                     :class="cellClass(index)"
                     :style="{ '--delay': delayMap[index] }"
        />
      </TransitionGroup>
    </div>
    <div v-if="!isValid">
      <div class="sentence"> {{ errorMessage }}</div>
    </div>
  </div>
</template>

<style scoped>

.list-enter-active {
  transition: opacity 0.7s ease;
  transition-delay: var(--delay, 0ms);
}


.list-enter-active :deep(.svg-display) {
  transition: opacity 0.7s ease, transform 0.7s ease;
  transition-delay: var(--delay, 0ms);
}

.list-leave-active {
  transition: opacity 0.3s ease;
  position: absolute;
}

.list-enter-from {
  opacity: 0;
}

.list-enter-from :deep(.svg-display) {
  opacity: 0;
  transform: scale(0.7);
}

.list-leave-to {
  opacity: 0;
}
</style>
